import { Observable } from 'rxjs';
import { isListItemElement, isOrderedListElement, ListElement } from '../../helpers/dom/list';
import { findAncestor } from '../../helpers/dom/node';
import { InteractionAction, InteractionHandler } from '../../interactionEvent';
import { ListService, ListType } from '../text/listService';
import { QuoteService } from '../text/quoteService';
import { TextUnderCursorService } from '../text/textUnderCursorService';
import { UndoService } from '../text/undoService';
import { createElementIndentAsYouTypeService } from './elementIndentAsYouTypeService';
import { ElementSelectionService } from './elementSelectionService';

export type ElementBlocksAsYouTypeServiceOptions = {
  onInteraction?: InteractionHandler;
  getHasListAutoFormatting?: () => boolean;
};

export function createElementBlocksAsYouTypeService(
  element$: Observable<Element | null>,
  selectionService: ElementSelectionService,
  paragraphTextUnderCursorService: TextUnderCursorService,
  blockquoteService: QuoteService,
  listService: ListService,
  undoService: UndoService,
  options: ElementBlocksAsYouTypeServiceOptions = {},
) {
  return createElementIndentAsYouTypeService(
    element$,
    selectionService,
    paragraphTextUnderCursorService,
    undoService,
    {
      markers: [
        {
          marker: /^>$/,
          indent: () => blockquoteService.quote(),
          action: InteractionAction.SET_BLOCKQUOTE,
        },
        {
          marker: /^[-*+]$/,
          indent: () => {
            if (listService.type) {
              return;
            }

            listService.setType(ListType.UNORDERED);
          },
          action: InteractionAction.SET_UNORDERED_LIST,
        },
        {
          marker: /^\d+\.$/,
          indent: marker => {
            if (listService.type) {
              return;
            }

            listService.setType(ListType.ORDERED);

            const markerNumber = parseOrderedListMarker(marker);
            setOrderedListItemNumber(selectionService, markerNumber);
          },
          action: InteractionAction.SET_ORDERED_LIST,
        },
      ],
      onInteraction: options.onInteraction,
      getHasListAutoFormatting: options.getHasListAutoFormatting,
    },
  );
}

function parseOrderedListMarker(marker: string): number {
  const match = marker.match(/^(\d+)\.$/);
  return match ? Number(match[1]) : NaN;
}

function setOrderedListItemNumber(selectionService: ElementSelectionService, itemNumber: number): void {
  if (!Number.isFinite(itemNumber)) {
    return;
  }

  // Make sure we have a simple cursor.
  const { container, selection } = selectionService;
  if (!container || !selection || !selection.isCollapsed) {
    return;
  }

  // Find the list item the cursor is in.
  const listItemElement = findAncestor(selection.focusNode, isListItemElement, container);
  if (!listItemElement) {
    return;
  }

  // Find the corresponding ordered list.
  const listElement = listItemElement.parentNode;
  if (!listElement || !isOrderedListElement(listElement)) {
    return;
  }

  // Change the list start number only if this is the first item.
  if (hasPrecedingListItems(listElement, listItemElement)) {
    return;
  }

  if (itemNumber === 1) {
    listElement.removeAttribute('start');
  } else {
    listElement.setAttribute('start', itemNumber.toString(10));
  }
}

function hasPrecedingListItems(listElement: ListElement, listItemElement: HTMLLIElement): boolean {
  for (const siblingNode of listElement.childNodes) {
    if (siblingNode === listItemElement) {
      break;
    }

    if (isListItemElement(siblingNode)) {
      return true;
    }
  }

  return false;
}
